import { useCallback, useEffect, useRef } from "react";
import {
  DockPosition,
  useBuildPreferencesStore,
} from "~/components/build/Show/lib/useBuildPreferencesStore";
import { debounce } from "lodash";
import { create } from "zustand";

export { DockPosition } from "./useBuildPreferencesStore";

interface AdjustedDockPositionStore {
  adjustedDockPosition: DockPosition;
  setAdjustedDockPosition: (dockPosition: DockPosition) => void;
}

/**
 * Internal zustand store to manage the adjusted dock position.
 *
 * This store holds the current adjusted dock position and provides a method to update it.
 * It is used to ensure that the dock position is responsive to container size changes.
 */
const useAdjustDockPositionStore = create<AdjustedDockPositionStore>()((set) => ({
  adjustedDockPosition: useBuildPreferencesStore.getState().dockPosition,
  setAdjustedDockPosition: (dockPosition: DockPosition) =>
    set({ adjustedDockPosition: dockPosition }),
}));

/**
 * Hook to get the current adjusted dock position based on the container width.
 */
export const useAdjustedDockPosition = () => {
  return useAdjustDockPositionStore((state) => state.adjustedDockPosition);
};

/**
 * Hook to adjust the dock position of the drawer based on the container width.
 *
 * It listens for container resize events and adjusts the dock position
 * based on the container width. If the container width is less than the minimum width, it changes the
 * dock position to Center. Otherwise, it maintains the current dock position.
 */
export const useAdjustDockPositionForContainer = (selector: string) => {
  const minWidth = 1120; // Same as @xl-bbui container breakpoint
  const containerRef = useRef<HTMLElement | null>(null);
  const dockPosition = useBuildPreferencesStore((state) => state.dockPosition);
  const adjustedDockPosition = useAdjustDockPositionStore((state) => state.adjustedDockPosition);
  const setAdjustedDockPosition = useAdjustDockPositionStore(
    (state) => state.setAdjustedDockPosition,
  );

  const debounceSetDockPosition = useCallback(
    debounce((position) => setAdjustedDockPosition(position), 150),
    [],
  );

  const handleContainerResize = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    if (containerRef.current.clientWidth < minWidth && dockPosition === DockPosition.Right) {
      debounceSetDockPosition(DockPosition.Center);
    } else {
      debounceSetDockPosition(dockPosition);
    }
  }, [dockPosition]);

  useEffect(() => {
    const container = document.querySelector<HTMLElement>(selector);
    if (container) {
      containerRef.current = container;
    }
  }, []);

  useEffect(() => {
    setAdjustedDockPosition(dockPosition);
  }, [dockPosition]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const observer = new ResizeObserver(handleContainerResize);
    observer.observe(containerRef.current);

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
      observer.disconnect();
    };
  }, [handleContainerResize]);

  return adjustedDockPosition;
};
